// Pane tree — the content area as a recursive n-ary tree of panes, pure.
//
// Split views (IDD 6ade2868, .claude/analysis/split-views-implementation.md §3)
// generalise the single tab strip (`Navigation/types.ts`) into a TREE of
// panes: each leaf is one pane (an ordered working set of tabs + which is
// active), and a branch is an orientation + an ordered list of children +
// their proportional sizes. This module is that pure model and its total
// reducer — the peer of `types.ts` the impl doc names.
//
// The through-line: the pane tree and its transitions are PURE data + pure
// folds; the DnD pointer capture, the resize pointer math, and persistence
// I/O are the impure shell (AppShellHost). This file introduces no new
// paradigm — it is `types.ts`'s single-strip model, lifted to a tree.
//
// Module topology (C1 hotspot decomposition): this entry file keeps the
// TREE-LEVEL transitions (the public folds the shell dispatches) and
// re-exports the substrate from the sibling `paneTree/` folder:
//  - `paneTree/types.ts`     — the pure data shapes + structural constants
//  - `paneTree/keys.ts`      — tab-key helpers + the ActiveTab bridge
//  - `paneTree/tree.ts`      — read/rebuild helpers + constructors
//  - `paneTree/leafOps.ts`   — leaf-local ops + the canonical ordering
//  - `paneTree/bridge.ts`    — TabSet ⇄ single-leaf PaneTree
//  - `paneTree/serialize.ts` — serialize / parse (parse-don't-validate)
//
// Discipline (dev-ts): pure — no I/O, no Date/random, no React; every varying
// shape is a named discriminated union on a `type` discriminant; folds are
// total, return fresh values, and return the SAME reference on a no-op so the
// host can skip a write. Ids are opaque pass-through `string` labels minted by
// the shell (the ResourceId convention) — the pure model never constructs a
// live one, so no brand (impl doc §3.2).

import { keyOf, tabHasKey } from "./paneTree/keys.js";
import {
    leafActivate,
    leafActivateKey,
    leafAppend,
    leafClosePreview,
    leafInsertAt,
    leafOpenPreview,
    leafPreviewEdited,
    leafPromotePreview,
    leafRemoveKey,
} from "./paneTree/leafOps.js";
import {
    findLeaf,
    focusedLeaf,
    leafIdOfKey,
    leavesOf,
    mapLeaf,
    normalizeSizes,
    reFocus,
} from "./paneTree/tree.js";
import {
    type Branch,
    type Leaf,
    MIN_PANE_FRACTION,
    type Orientation,
    type Pane,
    type PaneCloseOutcome,
    type PaneId,
    type PaneTree,
    type SplitEdge,
    type Tab,
    type TabKey,
} from "./paneTree/types.js";
import type { ResourceId, ResourceRef } from "./types.js";

// === splitPane — move a tab out of its pane into a new sibling ===============
// The moved tab LEAVES its origin pane. If the origin's parent branch already
// runs along the split orientation the new leaf is FLATTENED into it (the
// n-ary payoff — no needless nesting); otherwise the origin leaf is WRAPPED in
// a fresh branch. `newLeafId` / `newBranchId` are shell-minted (ids never in
// the pure fold). No-op when the pane / tab is missing, the tab is the home
// singleton, or the pane holds only that one tab (nothing to split off).
const splitAt = (
    pane: Pane,
    from: PaneId,
    moved: Tab,
    o: Orientation,
    before: boolean,
    newLeafId: PaneId,
    newBranchId: PaneId,
): Pane => {
    const newLeaf: Leaf = {
        type: "leaf",
        id: newLeafId,
        tabs: [moved],
        preview: null,
        active: keyOf(moved),
    };
    if (pane.type === "leaf") {
        // The origin is the root (no parent context) — wrap it.
        const children = before ? [newLeaf, pane] : [pane, newLeaf];
        return {
            type: "branch",
            id: newBranchId,
            orientation: o,
            children,
            sizes: [0.5, 0.5],
        };
    }
    const idx = pane.children.findIndex(
        (c) => c.type === "leaf" && c.id === from,
    );
    if (idx !== -1) {
        const target = pane.children[idx];
        if (target === undefined) return pane;
        if (pane.orientation === o) {
            // Flatten: split the target child's share between it and the new
            // leaf, inserted before/after it in this same n-ary branch.
            const pairs = pane.children.map((c, i) => ({
                c,
                s: pane.sizes[i] ?? 1 / pane.children.length,
            }));
            const half = (pairs[idx]?.s ?? 1 / pairs.length) / 2;
            const insertion = before
                ? [
                      { c: newLeaf, s: half },
                      { c: target, s: half },
                  ]
                : [
                      { c: target, s: half },
                      { c: newLeaf, s: half },
                  ];
            const next = [
                ...pairs.slice(0, idx),
                ...insertion,
                ...pairs.slice(idx + 1),
            ];
            return {
                ...pane,
                children: next.map((p) => p.c),
                sizes: normalizeSizes(next.map((p) => p.s)),
            };
        }
        // Perpendicular split: wrap the target child in a nested branch.
        const wrapped: Branch = {
            type: "branch",
            id: newBranchId,
            orientation: o,
            children: before ? [newLeaf, target] : [target, newLeaf],
            sizes: [0.5, 0.5],
        };
        return {
            ...pane,
            children: pane.children.map((c, i) => (i === idx ? wrapped : c)),
        };
    }
    // Recurse into the one child that contains `from`.
    return {
        ...pane,
        children: pane.children.map((c) =>
            findLeaf(c, from)
                ? splitAt(c, from, moved, o, before, newLeafId, newBranchId)
                : c,
        ),
    };
};

export const splitPane = (
    t: PaneTree,
    from: PaneId,
    tab: TabKey,
    edge: SplitEdge,
    newLeafId: PaneId,
    newBranchId: PaneId,
): PaneTree => {
    const src = findLeaf(t.root, from);
    if (src === undefined) return t;
    const moved = src.tabs.find((x) => tabHasKey(x, tab));
    if (moved === undefined) return t;
    if (src.tabs.length < 2) return t; // can't split a pane by its only tab
    const removed = mapLeaf(t.root, from, (l) => leafRemoveKey(l, tab));
    if (removed === null) return t;
    const o: Orientation =
        edge === "left" || edge === "right" ? "row" : "column";
    const before = edge === "left" || edge === "top";
    const root = splitAt(
        removed,
        from,
        moved,
        o,
        before,
        newLeafId,
        newBranchId,
    );
    return { root, focused: newLeafId };
};

// === moveTab — move/reorder a tab into a pane's strip (append zone) ==========
// Reorder within its own pane, or move across panes (pruning + collapsing the
// origin if it empties). Home never moves panes. No-op when the tab or target
// is missing.
export const moveTab = (
    t: PaneTree,
    tab: TabKey,
    to: PaneId,
    index: number,
): PaneTree => {
    const fromId = leafIdOfKey(t.root, tab);
    if (fromId === undefined || findLeaf(t.root, to) === undefined) return t;
    const moved = findLeaf(t.root, fromId)?.tabs.find((x) => tabHasKey(x, tab));
    if (moved === undefined) return t;
    if (fromId === to) {
        const root = mapLeaf(t.root, to, (l) => leafInsertAt(l, moved, index));
        return root === null || root === t.root ? t : { ...t, root };
    }
    const removed = mapLeaf(t.root, fromId, (l) => leafRemoveKey(l, tab));
    if (removed === null || findLeaf(removed, to) === undefined) return t;
    const root = mapLeaf(removed, to, (l) => leafInsertAt(l, moved, index));
    if (root === null) return t;
    return { root, focused: to };
};

// === openInFocused — open a tab into the focused pane (tree-wide dedup) ======
// Location model (Decision B): a tab already open in ANY pane is
// focused/activated there rather than duplicated; otherwise it appends to the
// focused pane and activates. The everything-is-a-tab openers (conversation,
// settings) are each their own caller (distinct-commands-per-intent).
export const openInFocused = (t: PaneTree, tab: Tab): PaneTree => {
    const key = keyOf(tab);
    const existing = leafIdOfKey(t.root, key);
    if (existing !== undefined) {
        const root = mapLeaf(t.root, existing, (l) => leafActivate(l, key));
        if (root === null) return t;
        return root === t.root && existing === t.focused
            ? t
            : { ...t, root, focused: existing };
    }
    const root = mapLeaf(t.root, t.focused, (l) => leafAppend(l, tab));
    return root === null || root === t.root ? t : { ...t, root };
};

// === Preview lifecycle on the focused pane (tree-level, pure) ================
// The shell's single-click / promote / close / edit gestures land here. Each
// targets the FOCUSED leaf (where the glance lives by construction) and returns
// the SAME reference on a no-op.

// The focused pane's current glance, or null — the render/strip reads this.
export const focusedPreview = (t: PaneTree): ResourceRef | null =>
    focusedLeaf(t).preview;

// Open a resource as a GLANCE (preview) in the focused pane. Tree-wide dedup
// (location model): a resource already KEPT in ANY pane is focused/activated
// there — never demoted to a glance; otherwise the focused pane's ONE slot
// adopts it, replacing any prior glance.
export const openPreviewInFocused = (
    t: PaneTree,
    ref: ResourceRef,
): PaneTree => {
    const key: TabKey = { type: "resource", id: ref.id };
    const existing = leafIdOfKey(t.root, key);
    if (existing !== undefined) {
        const root = mapLeaf(t.root, existing, (l) => leafActivate(l, key));
        if (root === null) return t;
        return root === t.root && existing === t.focused
            ? t
            : { ...t, root, focused: existing };
    }
    const root = mapLeaf(t.root, t.focused, (l) => leafOpenPreview(l, ref));
    return root === null || root === t.root ? t : { ...t, root };
};

// Promote the focused pane's preview to a kept tab (double-click the glance).
export const promotePreviewInFocused = (t: PaneTree): PaneTree => {
    const root = mapLeaf(t.root, t.focused, leafPromotePreview);
    return root === null || root === t.root ? t : { ...t, root };
};

// Close the focused pane's preview (× on the glance).
export const closePreviewInFocused = (t: PaneTree): PaneTree => {
    const root = mapLeaf(t.root, t.focused, leafClosePreview);
    return root === null || root === t.root ? t : { ...t, root };
};

// An edit committed on `id` promotes it if it is the focused pane's preview.
export const previewEditedInFocused = (
    t: PaneTree,
    id: ResourceId,
): PaneTree => {
    const root = mapLeaf(t.root, t.focused, (l) => leafPreviewEdited(l, id));
    return root === null || root === t.root ? t : { ...t, root };
};

// === activateInPane — activate a tab, moving focus to its pane ===============
export const activateInPane = (
    t: PaneTree,
    pane: PaneId,
    key: TabKey,
): PaneTree => {
    const leaf = findLeaf(t.root, pane);
    if (leaf === undefined || !leaf.tabs.some((x) => tabHasKey(x, key)))
        return t;
    const root = mapLeaf(t.root, pane, (l) => leafActivate(l, key));
    if (root === null) return t;
    return root === t.root && pane === t.focused
        ? t
        : { ...t, root, focused: pane };
};

// === activateKeyInFocused — activate a key in the FOCUSED pane, preview included
// Tree-level: activate `key` in the tree's FOCUSED pane. `key`'s shape is
// identical to `ActiveTab` (types.ts) member-for-member (`activeToKey` is the
// named bridge), so a caller holding either can pass it here directly —
// structurally, no cast.
export const activateKeyInFocused = (t: PaneTree, key: TabKey): PaneTree => {
    const root = mapLeaf(t.root, t.focused, (l) => leafActivateKey(l, key));
    return root === null || root === t.root ? t : { ...t, root };
};

// === closeTabInPane — the guarded close, lifted to the tree ==================
// Same dirty-guard as `types.ts`'s CloseOutcome, gaining a pane coordinate: a
// clean tab closes (pruning + collapsing an emptied pane), a dirty resource tab
// reports `unsaved`, the home singleton is unclosable (no-op). The tree-level
// payload carries the whole tree, not a TabSet.
export const closeTabInPane = (
    t: PaneTree,
    pane: PaneId,
    key: TabKey,
): PaneCloseOutcome => {
    const leaf = findLeaf(t.root, pane);
    const tab = leaf?.tabs.find((x) => tabHasKey(x, key));
    if (tab === undefined) return { type: "closed", tree: t };
    if (tab.type === "resource" && tab.dirty) return { type: "unsaved", tab };
    const root = mapLeaf(t.root, pane, (l) => leafRemoveKey(l, key));
    if (root === null) return { type: "closed", tree: t };
    return {
        type: "closed",
        tree: { root, focused: reFocus(root, t.focused) },
    };
};

// The confirmed discard — reachable only AFTER `closeTabInPane` reported
// `unsaved` (a distinct intent, never a `force` flag).
export const closeTabInPaneDiscarding = (
    t: PaneTree,
    pane: PaneId,
    key: TabKey,
): PaneTree => {
    const leaf = findLeaf(t.root, pane);
    if (leaf === undefined || !leaf.tabs.some((x) => tabHasKey(x, key)))
        return t;
    const root = mapLeaf(t.root, pane, (l) => leafRemoveKey(l, key));
    if (root === null) return t;
    return { root, focused: reFocus(root, t.focused) };
};

// === focusPane — move keyboard/new-tab focus to a leaf =======================
export const focusPane = (t: PaneTree, id: PaneId): PaneTree =>
    findLeaf(t.root, id) !== undefined && id !== t.focused
        ? { ...t, focused: id }
        : t;

// === resizeDivider — shift proportion between two adjacent children ==========
// `branch` names the branch owning the sash; `delta` is a proportion (the
// shell converted px→fraction at pointer-up). Moves mass between child i and
// i+1, clamping both to MIN_PANE_FRACTION — Σ stays 1.
const adjustBranch = (
    pane: Pane,
    branch: PaneId,
    dividerIndex: number,
    delta: number,
): Pane => {
    if (pane.type === "leaf") return pane;
    if (pane.id === branch) {
        const i = dividerIndex;
        const j = dividerIndex + 1;
        const a = pane.sizes[i];
        const b = pane.sizes[j];
        if (a === undefined || b === undefined) return pane;
        let na = a + delta;
        let nb = b - delta;
        if (na < MIN_PANE_FRACTION) {
            nb -= MIN_PANE_FRACTION - na;
            na = MIN_PANE_FRACTION;
        }
        if (nb < MIN_PANE_FRACTION) {
            na -= MIN_PANE_FRACTION - nb;
            nb = MIN_PANE_FRACTION;
        }
        if (na === a && nb === b) return pane;
        const sizes = [...pane.sizes];
        sizes[i] = na;
        sizes[j] = nb;
        return { ...pane, sizes };
    }
    let changed = false;
    const children = pane.children.map((c) => {
        const next = adjustBranch(c, branch, dividerIndex, delta);
        if (next !== c) changed = true;
        return next;
    });
    return changed ? { ...pane, children } : pane;
};

export const resizeDivider = (
    t: PaneTree,
    branch: PaneId,
    dividerIndex: number,
    delta: number,
): PaneTree => {
    const root = adjustBranch(t.root, branch, dividerIndex, delta);
    return root === t.root ? t : { ...t, root };
};

// === Focus stepping + structural helpers =====================================

// Whether the tree is a single leaf (no split) — the render fast-path: a
// single-leaf tree renders exactly as the pre-split single strip.
export const isSingleLeaf = (t: PaneTree): boolean => t.root.type === "leaf";

// Step focus to the next / previous leaf in in-order (strip) order, wrapping.
// A no-op (same reference) with one leaf.
const stepFocus = (t: PaneTree, step: 1 | -1): PaneTree => {
    const ls = leavesOf(t.root);
    if (ls.length <= 1) return t;
    const i = ls.findIndex((l) => l.id === t.focused);
    const next = ls[(Math.max(i, 0) + step + ls.length) % ls.length];
    return next !== undefined && next.id !== t.focused
        ? { ...t, focused: next.id }
        : t;
};
export const focusPaneNext = (t: PaneTree): PaneTree => stepFocus(t, 1);
export const focusPanePrevious = (t: PaneTree): PaneTree => stepFocus(t, -1);

// === closePane — collapse a (secondary) pane, its tabs merged into a sibling ==
// The pane's tabs relocate to the nearest sibling leaf (no data loss → not
// guarded), then the emptied leaf is pruned and the branch collapses. No-op on
// the only leaf, or on a pane that holds the unclosable home tab (the primary
// pane cannot be closed).
export const closePane = (t: PaneTree, id: PaneId): PaneTree => {
    const leaf = findLeaf(t.root, id);
    const ls = leavesOf(t.root);
    if (leaf === undefined || ls.length <= 1) return t;
    const i = ls.findIndex((l) => l.id === id);
    const target = ls[i - 1] ?? ls[i + 1];
    if (target === undefined) return t;
    // Move each tab to the sibling; the last move empties + prunes this leaf.
    let tree = t;
    for (const tab of leaf.tabs) {
        tree = moveTab(tree, keyOf(tab), target.id, Number.MAX_SAFE_INTEGER);
    }
    return findLeaf(tree.root, target.id) !== undefined
        ? { ...tree, focused: target.id }
        : tree;
};

export {
    emptyPaneTree,
    isEmptyPaneTree,
    MAIN_PANE,
    paneTreeFromTabSet,
    tabSetOf,
    tabSetOfPaneTree,
    withFocusedLeafTabs,
} from "./paneTree/bridge.js";
export { sameKey } from "./paneTree/keys.js";
export {
    leafCycleTab,
    leafLastTab,
    leafOrder,
    leafTabAtIndex,
} from "./paneTree/leafOps.js";
export { parsePaneTree, serializePaneTree } from "./paneTree/serialize.js";
export {
    findLeaf,
    focusedLeaf,
    leavesOf,
    seedPaneTree,
    singleLeafTree,
} from "./paneTree/tree.js";
// === Public surface — the sibling substrate re-exported at the stable path ===
export {
    type Branch,
    type Leaf,
    MAX_PANE_DEPTH,
    MIN_PANE_FRACTION,
    type Orientation,
    type Pane,
    type PaneCloseBlocked,
    type PaneCloseOutcome,
    type PaneId,
    type PaneTabClosed,
    type PaneTree,
    type SerializedPaneTree,
    type SplitEdge,
    type Tab,
    type TabKey,
} from "./paneTree/types.js";
